import { FCC } from "@/types/common";
import { Typography, Stack, styled, Link } from "@mui/material";
import { TextWithLinks } from "@/components/commons/TextWithLinks";
import { EMPTY_STR } from "@/constants/string";
import Divider from "@mui/material/Divider";
import { black, red, text } from "@/theme/colors";
import { convertNumberToZipCode } from "@/utils/input";

type TCompanyInfoItem = {
  label?: string;
  value?: string | null | undefined;
  direction?: "row" | "column";
  type?: string;
  color?: string;
  suffix?: string;
  required?: boolean;
  endIcon?: React.ReactNode;
  isShowDivider?: boolean;
};
export const CompanyInfoItem: FCC<TCompanyInfoItem> = ({
  label,
  value,
  direction,
  type = "string",
  color,
  suffix,
  endIcon,
  required = false,
  isShowDivider = true,
}) => {
  const getValue = () => {
    if (!value) return EMPTY_STR.TEXT;
    if (type === "zipcode") {
      return convertNumberToZipCode(value);
    }
    return value;
  };

  const RenderValue: FCC = ({ children }) => {
    return (
      <Typography
        variant="body14"
        color={color ? color : text.primary}
        sx={{
          fontFamily: "inherit",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
      >
        {children}
      </Typography>
    );
  };

  const getExternalLink = (link: string) => {
    let externalLink = String(link);
    if (!/^https?:\/\//i.test(externalLink)) {
      externalLink = `https://${externalLink}`;
    }
    return externalLink;
  };

  const RenderComponent: FCC = ({}) => {
    if (type === "link") {
      return (
        <RenderValue>
          {value ? (
            <Link href={getExternalLink(value)} target="_blank" underline="none" color={color} rel="noopener">
              {getValue()}
            </Link>
          ) : (
            getValue()
          )}
        </RenderValue>
      );
    }
    if (type === "textArea") {
      if (!value) return EMPTY_STR.TEXT;

      return (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 14,
            fontFamily: `"Noto Sans JP", sans-serif`,
          }}
        >
          <TextWithLinks text={value} />
        </pre>
      );
    }

    return (
      <RenderValue>
        {getValue()}
        {suffix && value ? suffix : ""}
      </RenderValue>
    );
  };

  return (
    <>
      <CompanyInfoItemWrapper direction={direction} gap={1}>
        {label && (
          <Typography variant="cap12" color={black[70]}>
            {label}
            {required && (
              <Typography component="span" variant="cap12" sx={{ color: red[700], ml: 0.5 }}>
                *
              </Typography>
            )}
          </Typography>
        )}
        <Stack direction="row" justifyContent="space-between">
          <RenderComponent />
          <Stack
            sx={{
              height: "20px",
            }}
          >
            {endIcon}
          </Stack>
        </Stack>
        {isShowDivider && <DividerWrapper />}
      </CompanyInfoItemWrapper>
    </>
  );
};

const CompanyInfoItemWrapper = styled(Stack)<{ direction?: string }>`
  ${({ direction }) => (direction === "row" ? `align-items: center;` : ``)}
`;

const DividerWrapper = styled(Divider)`
  margin: 16px 0;
  width: 100%;
`;
